import React, { useState, useEffect } from 'react'
import { BellIcon, BellSlashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
// import api from '../utils/api' // TODO: Update for new API structure

// VAPID public key - this should match your backend configuration
const VAPID_PUBLIC_KEY = 'BDd3_hVL9fZlyz8RRGUxIGVi6OWHwcYxCWfF8cO12QU3rEQ_0QPSxN1PjMSu9r6V1YqvjCCtCwYZq67u1qBWdCM'

const PushNotifications = ({ onClose }) => {
  const [isSupported, setIsSupported] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [preferences, setPreferences] = useState({
    daily_reminders: true,
    streak_reminders: true,
    badge_notifications: true,
    family_updates: true,
    emergency_drills: true,
    weekly_summaries: true
  })

  useEffect(() => {
    checkPushSupport()
    loadPreferences()
  }, [])

  const checkPushSupport = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true)
      
      try {
        const registration = await navigator.serviceWorker.register('/push-sw.js')
        const subscription = await registration.pushManager.getSubscription()
        setIsSubscribed(!!subscription)
      } catch (error) {
        console.error('Error checking push subscription:', error)
      }
    } else {
      setIsSupported(false)
    }
    setLoading(false)
  }

  const loadPreferences = async () => {
    try {
      const response = await api.get('/push/preferences')
      setPreferences(response.data)
    } catch (error) {
      console.error('Error loading preferences:', error)
    }
  }

  const subscribeToPush = async () => {
    try {
      setLoading(true)
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/push-sw.js')
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready
      
      // Request notification permission
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        throw new Error('Notification permission denied')
      }

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      })

      // Send subscription to backend
      await api.post('/push/subscribe', { 
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        }
      })

      setIsSubscribed(true)
      
      // Send test notification
      setTimeout(() => {
        sendTestNotification()
      }, 1000)
      
    } catch (error) {
      console.error('Error subscribing to push notifications:', error)
      alert('Failed to enable notifications: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const unsubscribeFromPush = async () => {
    try {
      setLoading(true)
      
      const registration = await navigator.serviceWorker.getRegistration('/push-sw.js')
      if (registration) {
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await subscription.unsubscribe()
          await api.post('/push/unsubscribe', { endpoint: subscription.endpoint })
        }
      }
      
      setIsSubscribed(false)
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error)
      alert('Failed to disable notifications')
    } finally {
      setLoading(false)
    }
  }

  const sendTestNotification = async () => {
    try {
      await api.post('/push/test')
    } catch (error) {
      console.error('Error sending test notification:', error)
    }
  }

  const updatePreferences = async (newPreferences) => {
    try {
      await api.put('/push/preferences', newPreferences)
      setPreferences(newPreferences)
    } catch (error) {
      console.error('Error updating preferences:', error)
      alert('Failed to update preferences')
    }
  }

  const handlePreferenceChange = (key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    }
    updatePreferences(newPreferences)
  }

  // Utility functions
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading notification settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <BellIcon className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Notifications</h2>
                <p className="opacity-90">Stay prepared with smart alerts</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto">
          {!isSupported ? (
            <div className="text-center py-8">
              <BellSlashIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Push Notifications Not Supported
              </h3>
              <p className="text-gray-600">
                Your browser doesn't support push notifications. Try using a modern browser like Chrome, Firefox, or Safari.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enable/Disable Notifications */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  {isSubscribed ? (
                    <BellIcon className="h-6 w-6 text-green-600" />
                  ) : (
                    <BellSlashIcon className="h-6 w-6 text-gray-400" />
                  )}
                  <div>
                    <h3 className="font-semibold">
                      {isSubscribed ? 'Notifications Enabled' : 'Enable Notifications'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {isSubscribed 
                        ? 'You\'ll receive survival reminders and updates' 
                        : 'Get daily reminders and emergency alerts'
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
                  disabled={loading}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isSubscribed
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  } disabled:opacity-50`}
                >
                  {loading ? 'Working...' : isSubscribed ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Test Notification */}
              {isSubscribed && (
                <div className="text-center">
                  <button
                    onClick={sendTestNotification}
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                  >
                    Send Test Notification
                  </button>
                </div>
              )}

              {/* Notification Preferences */}
              {isSubscribed && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Cog6ToothIcon className="h-5 w-5" />
                    Notification Types
                  </h3>
                  
                  <div className="space-y-3">
                    {[
                      {
                        key: 'daily_reminders',
                        title: 'Daily Reminders',
                        description: 'Get reminded to complete daily survival tasks'
                      },
                      {
                        key: 'streak_reminders',
                        title: 'Streak Alerts',
                        description: 'Don\'t break your learning streak!'
                      },
                      {
                        key: 'badge_notifications',
                        title: 'Achievement Badges',
                        description: 'Celebrate when you earn new badges'
                      },
                      {
                        key: 'family_updates',
                        title: 'Family Updates',
                        description: 'New challenges and family member activities'
                      },
                      {
                        key: 'emergency_drills',
                        title: 'Emergency Drills',
                        description: 'Random survival scenario tests (Chaos Mode)'
                      },
                      {
                        key: 'weekly_summaries',
                        title: 'Weekly Progress',
                        description: 'Summary of your week\'s survival training'
                      }
                    ].map((pref) => (
                      <div key={pref.key} className="flex items-center justify-between py-3 border-b">
                        <div>
                          <h4 className="font-medium">{pref.title}</h4>
                          <p className="text-sm text-gray-600">{pref.description}</p>
                        </div>
                        <button
                          onClick={() => handlePreferenceChange(pref.key)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            preferences[pref.key] ? 'bg-orange-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              preferences[pref.key] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">💡 Smart Notifications</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Notifications respect your device's "Do Not Disturb" settings</li>
                  <li>• Emergency drills only activate when Chaos Mode is enabled</li>
                  <li>• You can disable notifications anytime from this screen</li>
                  <li>• All data stays private and secure</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PushNotifications